
import { useState, useEffect } from "react";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";
import { getWordForDay } from "@/lib/wordLibrary";
import { MessageCircle, User, Book, Plus, ArrowRight, Target, Trophy, Award, Calendar, Shuffle } from "lucide-react";
import { findNewLetterIndex, getNewLetter } from "@/lib/types/WordSequence";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Header } from "@/components/layout/Header";

const getExampleSequence = () => {
  const todaySequence = getWordForDay();
  const randomDayOffset = Math.floor(Math.random() * 30) + 1;
  const randomDate = new Date();
  randomDate.setDate(randomDate.getDate() + randomDayOffset);
  const exampleSequence = getWordForDay(randomDate);

  if (exampleSequence[0] === todaySequence[0]) {
    randomDate.setDate(randomDate.getDate() + 15);
    return getWordForDay(randomDate);
  }
  return exampleSequence;
};

const HowToPlay = () => {
  const [exampleWords, setExampleWords] = useState<string[]>([]);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    setExampleWords(getExampleSequence());
  }, []);

  const level1NewLetterIndex = 0;
  const level2NewLetterIndex = exampleWords.length >= 2 ? findNewLetterIndex(exampleWords[0], exampleWords[1]) : 0;
  const level3NewLetterIndex = exampleWords.length >= 3 ? findNewLetterIndex(exampleWords[1], exampleWords[2]) : 0;
  const level4NewLetterIndex = exampleWords.length >= 4 ? findNewLetterIndex(exampleWords[2], exampleWords[3]) : 0;

  const level2NewLetter = exampleWords.length >= 2 ? getNewLetter(exampleWords[0], exampleWords[1]) : '';
  const level3NewLetter = exampleWords.length >= 3 ? getNewLetter(exampleWords[1], exampleWords[2]) : '';
  const level4NewLetter = exampleWords.length >= 4 ? getNewLetter(exampleWords[2], exampleWords[3]) : '';

  const GameModesSection = () => (
    <div className="space-y-6">
      {/* Daily Challenge Mode */}
      <Card className="bg-gray-800 border-gray-700 overflow-hidden">
        <div className="bg-gradient-to-r from-[#de0046] to-[#ff1155] h-2"></div>
        <CardContent className="p-5">
          <h3 className="text-xl font-bold text-gray-100 mb-4 flex items-center gap-2">
            <Calendar className="text-[#de0046]" size={22} />
            <span>Daily Challenge</span>
          </h3>
          
          <div className="space-y-3">
            <p className="text-gray-300">
              The main game mode where everyone gets the same word sequence each day.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-700">
                <h4 className="text-sm font-medium text-[#de0046] mb-1">Streak System</h4>
                <p className="text-xs text-gray-300">Win consecutive days to unlock BabbleRanks and build your streak.</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-700">
                <h4 className="text-sm font-medium text-[#de0046] mb-1">One Chance</h4>
                <p className="text-xs text-gray-300">You get one attempt per day - make it count!</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Practice Mode */}
      <Card className="bg-gray-800 border-gray-700 overflow-hidden">
        <div className="bg-gradient-to-r from-[#8B5CF6] to-[#a855f7] h-2"></div>
        <CardContent className="p-5">
          <h3 className="text-xl font-bold text-gray-100 mb-4 flex items-center gap-2">
            <Shuffle className="text-[#8B5CF6]" size={22} />
            <span>Practice Mode</span>
          </h3>
          
          <div className="space-y-3">
            <p className="text-gray-300">
              Perfect for improving your skills with unlimited gameplay.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-700">
                <h4 className="text-sm font-medium text-[#8B5CF6] mb-1">Unlimited Play</h4>
                <p className="text-xs text-gray-300">Play as many games as you want with random sequences.</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-700">
                <h4 className="text-sm font-medium text-[#8B5CF6] mb-1">Share Games</h4>
                <p className="text-xs text-gray-300">Share your practice sequences with others via URL.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const BabbleRanksSection = () => (
    <Card className="bg-gray-800 border-gray-700 overflow-hidden">
      <div className="bg-gradient-to-r from-[#8B5CF6] to-[#de0046] h-2"></div>
      <CardContent className="p-5">
        <h2 className="text-xl font-bold text-gray-100 mb-4 flex items-center gap-2">
          <Trophy className="text-[#de0046]" size={22} />
          <span>BabbleRanks</span>
        </h2>
        
        <div className="space-y-4">
          <p className="text-gray-300">
            Win consecutive days in <span className="text-[#de0046] font-semibold">Daily Challenge</span> to unlock higher BabbleRanks. Each rank comes with a unique title and icon!
          </p>
          
          <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <Award className="text-[#de0046]" size={18} />
              <span className="text-sm font-medium text-gray-200">Unlock 30 unique ranks!</span>
            </div>
            <p className="text-xs text-gray-300">From "Baby Babbler" to "Babblegram Grandmaster" - how far can you go?</p>
          </div>
          
          <div className="bg-orange-900/20 border border-orange-600/30 rounded-lg p-3">
            <p className="text-orange-300 text-xs">
              <strong>Note:</strong> BabbleRanks progress only in Daily Challenge mode. Practice games show temporary streaks that don't count toward your permanent ranking.
            </p>
          </div>
          
          <Button asChild className="w-full bg-[#8B5CF6] hover:bg-[#7c49f5] border-none">
            <Link to="/babbleranks" className="flex items-center justify-center gap-2">
              <span>View All Ranks</span>
              <ArrowRight size={16} />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const ColorGuideSection = () => (
    <div className="grid grid-cols-3 gap-1.5 md:gap-2 mb-5 bg-gray-700/50 p-3 rounded-lg">
      <div className="flex items-center gap-1.5">
        <div className="relative w-5 min-w-[20px] md:w-7 md:min-w-[28px]">
          <AspectRatio ratio={1/1}>
            <div className="absolute inset-0 bg-success rounded flex items-center justify-center text-white font-bold text-xs md:text-sm">A</div>
          </AspectRatio>
        </div>
        <span className="text-gray-300 text-[10px] md:text-xs whitespace-nowrap">Correct spot</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="relative w-5 min-w-[20px] md:w-7 md:min-w-[28px]">
          <AspectRatio ratio={1/1}>
            <div className="absolute inset-0 bg-warning rounded flex items-center justify-center text-white font-bold text-xs md:text-sm">B</div>
          </AspectRatio>
        </div>
        <span className="text-gray-300 text-[10px] md:text-xs whitespace-nowrap">Wrong spot</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="relative w-5 min-w-[20px] md:w-7 md:min-w-[28px]">
          <AspectRatio ratio={1/1}>
            <div className="absolute inset-0 bg-destructive rounded flex items-center justify-center text-white font-bold text-xs md:text-sm">C</div>
          </AspectRatio>
        </div>
        <span className="text-gray-300 text-[10px] md:text-xs whitespace-nowrap">Not in word</span>
      </div>
    </div>
  );

  const LevelExampleRow = ({ 
    level, 
    word, 
    newLetterIndex, 
    newLetter, 
    color, 
    length 
  }: { 
    level: number; 
    word?: string; 
    newLetterIndex: number; 
    newLetter: string; 
    color: string; 
    length: number;
  }) => {
    const tileSize = isMobile ? "w-7 h-7 md:w-10 md:h-10" : "w-10 h-10";
    const tileGap = isMobile ? "gap-1 md:gap-1.5" : "gap-1.5";
    
    return (
      <div className="mb-3 flex items-start md:items-center">
        <div className="flex-shrink-0 mr-2 md:mr-4 min-w-max">
          <div className={`flex ${tileGap}`}>
            {Array.from({length}).map((_, i) => (
              <div 
                key={i} 
                className={`${tileSize} bg-success rounded flex items-center justify-center text-white font-bold text-sm md:text-base`}
                style={level > 1 && i === newLetterIndex ? {
                  border: '2px solid #de0046',
                  boxShadow: '0 0 5px #de0046',
                  position: 'relative',
                  zIndex: 5
                } : undefined}
              >
                {word?.[i]?.toUpperCase()}
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1 h-7 md:h-10 flex flex-col justify-center">
          <h3 className={`${color} font-bold text-sm md:text-lg leading-tight`}>Level {level}</h3>
          <p className="text-gray-300 text-xs md:text-sm leading-tight whitespace-nowrap">
            {length}-letter word {level > 1 && <span>• <span className="text-[#de0046] font-bold">{newLetter}</span> is new</span>}
          </p>
        </div>
      </div>
    );
  };

  const LevelExamplesSection = () => (
    <div className="bg-gray-700/50 rounded-lg p-3 md:p-4">
      <LevelExampleRow 
        level={1} 
        word={exampleWords[0]} 
        newLetterIndex={level1NewLetterIndex} 
        newLetter="" 
        color="text-[#de0046]" 
        length={3} 
      />
      
      <LevelExampleRow 
        level={2} 
        word={exampleWords[1]} 
        newLetterIndex={level2NewLetterIndex} 
        newLetter={level2NewLetter} 
        color="text-[#8B5CF6]" 
        length={4} 
      />
      
      <LevelExampleRow 
        level={3} 
        word={exampleWords[2]} 
        newLetterIndex={level3NewLetterIndex} 
        newLetter={level3NewLetter} 
        color="text-[#0EA5E9]" 
        length={5} 
      />
      
      <LevelExampleRow 
        level={4} 
        word={exampleWords[3]} 
        newLetterIndex={level4NewLetterIndex} 
        newLetter={level4NewLetter} 
        color="text-[#22C55E]" 
        length={6} 
      />
    </div>
  );

  const GameInstructionsSection = () => (
    <div className="bg-gray-800 rounded-xl p-5 shadow-lg border border-gray-700">
      <h2 className="text-xl font-bold text-gray-100 mb-4 flex items-center gap-2">
        <Book className="text-white" size={20} />
        How to Play
      </h2>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="flex flex-col items-center justify-start bg-gray-700/50 rounded-lg p-3 h-full">
          <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center mb-2">
            <Book className="h-5 w-5 text-white" />
          </div>
          <p className="text-[#de0046] font-bold text-sm text-center mb-1">4 Puzzles</p>
          <p className="text-gray-300 text-xs text-center">Solve word chain sequence</p>
        </div>
        
        <div className="flex flex-col items-center justify-start bg-gray-700/50 rounded-lg p-3 h-full">
          <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center mb-2">
            <Plus className="h-5 w-5 text-white" />
          </div>
          <p className="text-[#de0046] font-bold text-sm text-center mb-1">New Letter</p>
          <p className="text-gray-300 text-xs text-center">Extra letter each level</p>
        </div>
        
        <div className="flex flex-col items-center justify-start bg-gray-700/50 rounded-lg p-3 h-full">
          <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center mb-2">
            <ArrowRight className="h-5 w-5 text-white" />
          </div>
          <p className="text-[#de0046] font-bold text-sm text-center mb-1">Progress</p>
          <p className="text-gray-300 text-xs text-center">Fewer tries each level</p>
        </div>
        
        <div className="flex flex-col items-center justify-start bg-gray-700/50 rounded-lg p-3 h-full">
          <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center mb-2">
            <Target className="h-5 w-5 text-white" />
          </div>
          <p className="text-[#de0046] font-bold text-sm text-center mb-1">Build Streak</p>
          <p className="text-gray-300 text-xs text-center">Daily wins unlock ranks</p>
        </div>
      </div>

      <h3 className="text-lg font-bold text-gray-200 mb-2">Color Guide</h3>
      <ColorGuideSection />

      <h3 className="text-lg font-bold text-gray-200 mb-3">Level Examples</h3>
      <LevelExamplesSection />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col relative overflow-x-hidden">
      <SEO
        title="How to Play BabbleGram – Rules & Tips"
        description="Learn how to play BabbleGram: solve 3, 4, and 5-letter daily word puzzles where each level builds on the previous answer."
        path="/how-to-play"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to play BabbleGram",
          step: [
            { "@type": "HowToStep", name: "Solve the 3-letter word", text: "Guess the 3-letter starting word." },
            { "@type": "HowToStep", name: "Solve the 4-letter word", text: "Use the previous answer plus one new letter to find the 4-letter word." },
            { "@type": "HowToStep", name: "Solve the 5-letter word", text: "Build on the 4-letter word with one more new letter to finish the puzzle." }
          ]
        }}
      />
      <Header />

      <main className="flex-1 container max-w-4xl mx-auto px-4 py-8 pt-24 relative z-10">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#de0046]">How To Play</h1>
        </div>
        
        <div className="space-y-6">
          <GameModesSection />
          
          {isMobile ? (
            <>
              <GameInstructionsSection />
              <BabbleRanksSection />
            </>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-1 space-y-5">
                <BabbleRanksSection />
              </div>
              <div className="md:col-span-2">
                <GameInstructionsSection />
              </div>
            </div>
          )}
        </div>
        
        <div className="text-gray-500 text-xs text-center w-full mt-8">
          © {new Date().getFullYear()} BabbleGram. Created by Dara Walsh
        </div>
      </main>
    </div>
  );
};

export default HowToPlay;
